import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Clock, Play, Square, ChevronLeft, ChevronRight, Plus, Trash2,
  X, Loader2, Calendar, Timer, CheckCircle
} from "lucide-react";

// Day names
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Format time from ISO string
const formatTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Format duration in minutes to hours:minutes
const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
};

// Format elapsed time for live counter
const formatElapsed = (startTime) => {
  if (!startTime) return "00:00:00";
  const start = new Date(startTime);
  const now = new Date();
  const diff = Math.floor((now - start) / 1000);
  
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// Current time display
const CurrentTime = () => {
  const [time, setTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  return (
    <div className="text-4xl sm:text-5xl font-mono font-bold text-[#1e3a5f]">
      {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
    </div>
  );
};

// Elapsed time counter
const ElapsedTimer = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(formatElapsed(startTime));
  
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(formatElapsed(startTime));
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);
  
  return (
    <div className="text-3xl sm:text-4xl font-mono font-bold text-emerald-600">
      {elapsed}
    </div>
  );
};

// Time Entry Row
const TimeEntryRow = ({ entry, onDelete }) => {
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
      <div className="flex items-center gap-4">
        <div className="text-sm">
          <span className="font-medium text-[#1e3a5f]">{formatTime(entry.clock_in)}</span>
          <span className="text-slate-400 mx-2">→</span>
          <span className={`font-medium ${entry.clock_out ? "text-[#1e3a5f]" : "text-emerald-600"}`}>
            {entry.clock_out ? formatTime(entry.clock_out) : "Active"}
          </span>
        </div>
        {entry.is_manual && (
          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Manual</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-600">
          {entry.duration_minutes ? formatDuration(entry.duration_minutes) : "-"}
        </span>
        {entry.clock_out && (
          <button
            onClick={() => onDelete(entry)}
            className="p-1 text-slate-400 hover:text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Manual Entry Modal
const ManualEntryModal = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: "09:00",
    end_time: "17:00",
    notes: ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.start_time >= formData.end_time) {
      toast.error("End time must be after start time");
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="manual-entry-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#1e3a5f]">Add Manual Entry</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="border-slate-200"
              data-testid="manual-entry-date"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="border-slate-200"
                data-testid="manual-entry-start"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="border-slate-200"
                data-testid="manual-entry-end"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes (Optional)</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="e.g., Forgot to clock in"
              className="border-slate-200 resize-none"
              rows={2}
              data-testid="manual-entry-notes"
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45]"
              data-testid="manual-entry-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Entry
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Weekly Timesheet
const WeeklyTimesheet = ({ weekData, onPrevWeek, onNextWeek, onThisWeek }) => {
  const weekRange = weekData.week_start && weekData.week_end
    ? `${formatDate(weekData.week_start)} - ${formatDate(weekData.week_end)}`
    : "";
  
  const days = weekData.days ? Object.values(weekData.days).sort((a, b) => a.date.localeCompare(b.date)) : [];
  
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-[#1e3a5f]">Weekly Timesheet</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onPrevWeek}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium text-slate-600 min-w-[140px] text-center">{weekRange}</span>
            <Button variant="ghost" size="sm" onClick={onNextWeek}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onThisWeek} className="text-xs text-[#d4a853] w-fit">
          This Week
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 font-medium text-slate-500">Day</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Date</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Clock In</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Clock Out</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Hours</th>
              </tr>
            </thead>
            <tbody>
              {days.map((day) => (
                <React.Fragment key={day.date}>
                  {day.entries.length === 0 ? (
                    <tr className={`border-b border-slate-100 ${day.is_today ? "bg-[#d4a853]/5" : ""}`}>
                      <td className="py-2 px-3 font-medium text-slate-700">
                        {day.day_name}
                        {day.is_today && (
                          <span className="ml-2 text-[10px] bg-[#d4a853] text-white px-1.5 py-0.5 rounded">Today</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-slate-500">{formatDate(day.date)}</td>
                      <td className="py-2 px-3 text-slate-400">-</td>
                      <td className="py-2 px-3 text-slate-400">-</td>
                      <td className="py-2 px-3 text-right text-slate-400">0h</td>
                    </tr>
                  ) : (
                    day.entries.map((entry, idx) => (
                      <tr 
                        key={entry.id} 
                        className={`border-b border-slate-100 ${day.is_today ? "bg-[#d4a853]/5" : ""}`}
                      >
                        <td className="py-2 px-3 font-medium text-slate-700">
                          {idx === 0 && (
                            <>
                              {day.day_name}
                              {day.is_today && (
                                <span className="ml-2 text-[10px] bg-[#d4a853] text-white px-1.5 py-0.5 rounded">Today</span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="py-2 px-3 text-slate-500">{idx === 0 ? formatDate(day.date) : ""}</td>
                        <td className="py-2 px-3 text-slate-600">{formatTime(entry.clock_in)}</td>
                        <td className="py-2 px-3 text-slate-600">
                          {entry.clock_out ? formatTime(entry.clock_out) : (
                            <span className="text-emerald-600">Active</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-medium text-[#1e3a5f]">
                          {idx === 0 ? `${day.total_hours}h` : formatDuration(entry.duration_minutes)}
                        </td>
                      </tr>
                    ))
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50">
                <td colSpan={4} className="py-3 px-3 font-semibold text-[#1e3a5f]">Week Total</td>
                <td className="py-3 px-3 text-right font-bold text-[#1e3a5f] text-lg">
                  {weekData.week_total_hours || 0}h
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty State
const EmptyState = () => (
  <div className="text-center py-8">
    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
      <Timer className="w-6 h-6 text-slate-400" />
    </div>
    <p className="text-slate-500 text-sm">No time entries yet today.</p>
    <p className="text-slate-400 text-xs">Clock in to start tracking.</p>
  </div>
);

// Main Component
export default function ProTimeClockPage() {
  const { token } = useAuth();
  const [status, setStatus] = useState({ is_clocked_in: false, active_entry: null });
  const [todayEntries, setTodayEntries] = useState([]);
  const [weekData, setWeekData] = useState({});
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [weekDate, setWeekDate] = useState(null);

  // Get today's date string
  const today = new Date().toISOString().split('T')[0];

  // Fetch clock status
  const fetchStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/pro/timeclock/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch status");
    }
  }, [token]);

  // Fetch today's entries
  const fetchTodayEntries = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/pro/timeclock/entries?start_date=${today}&end_date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayEntries(response.data.entries || []);
    } catch (error) {
      console.error("Failed to fetch today's entries");
    }
  }, [token, today]);

  // Fetch week summary
  const fetchWeekSummary = useCallback(async () => {
    if (!token) return;
    
    try {
      const dateParam = weekDate ? `?week=${weekDate}` : "";
      const response = await axios.get(`${API}/pro/timeclock/summary${dateParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeekData(response.data);
    } catch (error) {
      console.error("Failed to fetch week summary");
    } finally {
      setLoading(false);
    }
  }, [token, weekDate]);

  useEffect(() => {
    fetchStatus();
    fetchTodayEntries();
    fetchWeekSummary();
  }, [fetchStatus, fetchTodayEntries, fetchWeekSummary]);

  // Clock in
  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      await axios.post(`${API}/pro/timeclock/clockin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Clocked in!");
      fetchStatus();
      fetchTodayEntries();
      fetchWeekSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock in");
    } finally {
      setClockingIn(false);
    }
  };

  // Clock out
  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      await axios.post(`${API}/pro/timeclock/clockout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Clocked out!");
      fetchStatus();
      fetchTodayEntries();
      fetchWeekSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock out");
    } finally {
      setClockingOut(false);
    }
  };

  // Add manual entry
  const handleAddManual = async (data) => {
    try {
      await axios.post(`${API}/pro/timeclock/manual`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Manual entry added");
      fetchTodayEntries();
      fetchWeekSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add entry");
      throw error;
    }
  };

  // Delete entry
  const handleDeleteEntry = async (entry) => {
    if (!window.confirm("Delete this time entry?")) return;
    
    try {
      await axios.delete(`${API}/pro/timeclock/${entry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Entry deleted");
      fetchTodayEntries();
      fetchWeekSummary();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  // Week navigation
  const goToPrevWeek = () => {
    const currentWeekStart = weekData.week_start ? new Date(weekData.week_start) : new Date();
    currentWeekStart.setDate(currentWeekStart.getDate() - 7);
    setWeekDate(currentWeekStart.toISOString().split('T')[0]);
  };

  const goToNextWeek = () => {
    const currentWeekStart = weekData.week_start ? new Date(weekData.week_start) : new Date();
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    setWeekDate(currentWeekStart.toISOString().split('T')[0]);
  };

  const goToThisWeek = () => {
    setWeekDate(null);
  };

  // Calculate today's total
  const todayTotalMinutes = todayEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);

  return (
    <div className="space-y-6" data-testid="pro-timeclock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Time Clock</h1>
          <p className="text-slate-500">Track your work hours</p>
        </div>
        
        <Button
          onClick={() => setShowManualModal(true)}
          variant="outline"
          className="border-slate-200"
          data-testid="add-manual-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Manual Entry
        </Button>
      </div>

      {/* Main Clock Card */}
      <Card className={`border-2 ${status.is_clocked_in ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`}>
        <CardContent className="p-6 sm:p-8">
          <div className="text-center space-y-6">
            {/* Current Time */}
            <div>
              <p className="text-sm text-slate-500 mb-2">Current Time</p>
              <CurrentTime />
            </div>
            
            {/* Status */}
            <div className="flex items-center justify-center gap-2">
              <span className={`w-3 h-3 rounded-full ${status.is_clocked_in ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
              <span className={`text-lg font-medium ${status.is_clocked_in ? "text-emerald-600" : "text-slate-500"}`}>
                {status.is_clocked_in ? "Working" : "Not Working"}
              </span>
            </div>
            
            {/* Elapsed Time (when clocked in) */}
            {status.is_clocked_in && status.active_entry && (
              <div>
                <p className="text-sm text-slate-500 mb-1">
                  Clocked in since {formatTime(status.active_entry.clock_in)}
                </p>
                <ElapsedTimer startTime={status.active_entry.clock_in} />
              </div>
            )}
            
            {/* Clock In/Out Button */}
            <div>
              {status.is_clocked_in ? (
                <Button
                  onClick={handleClockOut}
                  disabled={clockingOut}
                  className="w-full sm:w-64 h-16 text-xl bg-red-500 hover:bg-red-600 text-white"
                  data-testid="clock-out-btn"
                >
                  {clockingOut ? (
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  ) : (
                    <Square className="w-6 h-6 mr-2" />
                  )}
                  Clock Out
                </Button>
              ) : (
                <Button
                  onClick={handleClockIn}
                  disabled={clockingIn}
                  className="w-full sm:w-64 h-16 text-xl bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="clock-in-btn"
                >
                  {clockingIn ? (
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  ) : (
                    <Play className="w-6 h-6 mr-2" />
                  )}
                  Clock In
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Today's Summary */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#d4a853]" />
              Today's Summary
            </CardTitle>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#1e3a5f]">{formatDuration(todayTotalMinutes)}</p>
              <p className="text-xs text-slate-500">Total Today</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#d4a853] mx-auto" />
            </div>
          ) : todayEntries.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-2">
              {todayEntries.map((entry) => (
                <TimeEntryRow key={entry.id} entry={entry} onDelete={handleDeleteEntry} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Weekly Timesheet */}
      {!loading && (
        <WeeklyTimesheet
          weekData={weekData}
          onPrevWeek={goToPrevWeek}
          onNextWeek={goToNextWeek}
          onThisWeek={goToThisWeek}
        />
      )}

      {/* Manual Entry Modal */}
      {showManualModal && (
        <ManualEntryModal
          onSave={handleAddManual}
          onClose={() => setShowManualModal(false)}
        />
      )}
    </div>
  );
}
